import {
  AfterContentChecked,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import diacritic from 'diacritic';

import { debounce } from 'rxjs/operators';
import { interval } from 'rxjs';

import { KeyboardEnum } from '~/component/enums/keyboard.enum';
import { InputStyleEnum } from '~/component/form/input/enums/style.enum';
import { ValueAccessorBase } from '~/component/value-accessor.base';
import { generateUid } from '~/core/helpers/uid/uid.helper';

import { AutocompleteV2Suggestion } from './interfaces/suggestion.interface';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonSizeEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-size.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';

@Component({
  selector: 'app-form-autocomplete-v2',
  templateUrl: './autocomplete-v2.component.html',
  styleUrls: ['./autocomplete-v2.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormAutocompleteV2Component,
      multi: true,
    },
  ],
})
export class FormAutocompleteV2Component
  extends ValueAccessorBase<{
    text: string;
    value: string | number;
  }>
  implements OnInit, OnChanges, AfterContentChecked
{
  @ContentChild('listContent') public listContent: ElementRef;

  @Input() public debounceBehavior?: boolean;
  @Input() public autoFilter? = true;
  @Input() public clearButtonAriaLabel: string;
  @Input() public iconName?: VtmnPlayIconTypeEnum;
  @Input() public inputStyle: InputStyleEnum;
  @Input() public items: { text: string; value: string | number }[] = [];
  @Input() public placeholder? = '';

  @Output() public readonly clearInputEvent = new EventEmitter<string>();
  @Output() public readonly focusInputEvent = new EventEmitter();
  @Output() public readonly selectItemEvent = new EventEmitter<{
    text: string;
    value: string | number;
  }>();
  @Output() public readonly valueChangeEvent = new EventEmitter();

  @ViewChildren('listItem') public listItem: QueryList<ElementRef>;

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayIconButtonSize = VtmnPlayIconButtonSizeEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;

  public filteredItems: { text: string; value: string | number }[];
  public focusItemId: string;

  public focusItemIndex: number;
  public hasContent = false;
  public id: string = generateUid();
  public input = new FormControl();
  public isExpanded = false;

  constructor(private readonly autocompleteComponent: ElementRef) {
    super();
  }

  public ngAfterContentChecked(): void {
    this.hasContent = Boolean(this.listContent?.nativeElement);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.items) {
      this.filteredItems = changes.items.currentValue;
    }
  }

  public ngOnInit(): void {
    this.filteredItems = this.items;
    this.input.valueChanges
      .pipe(
        debounce(() => {
          let timer = 0;
          if (this.debounceBehavior) {
            timer = 400;
          }

          return interval(timer);
        })
      )
      .subscribe((text) => {
        if (!this.autoFilter) {
          this.valueChangeEvent.emit(text);

          return;
        }

        this.filterItems(text);
      });
  }

  public get inputClass(): string {
    switch (this.inputStyle) {
      case InputStyleEnum.Shadowed:
        return 'autocomplete-v2--shadowed';
      case InputStyleEnum.Transparent:
        return 'autocomplete-v2--transparent';
      default:
        return '';
    }
  }

  public get listboxId(): string {
    return `listbox-${this.id}`;
  }

  @HostListener('document:click', ['$event.target'])
  private clickout(target): void {
    if (
      !this.autocompleteComponent.nativeElement.contains(target) &&
      this.isExpanded
    ) {
      this.isExpanded = false;
      this.preventCustomValue();
    }
  }

  private preventCustomValue(): void {
    if (!this.input.value) {
      return this.clearInput();
    }

    this.input.setValue(this.value?.text);
  }

  public clearInput(): void {
    this.value = null;
    this.input.setValue(null);
    this.isExpanded = false;

    if (!this.autoFilter) {
      this.clearInputEvent.emit();
    }
  }

  public displayListbox(): void {
    this.isExpanded = true;
    this.focusItemIndex = 0;

    if (!this.autoFilter) {
      this.focusInputEvent.emit();
    }
  }

  public filterItems(text: string): void {
    if (!text) {
      this.filteredItems = this.items;
      return;
    }

    this.filteredItems = this.items.filter((item) =>
      diacritic
        .clean(item.text)
        .toLowerCase()
        .includes(diacritic.clean(text.toLowerCase()))
    );
  }

  public getItemId(index: number): string {
    return `item-${index}-${this.id}`;
  }

  public isFocus(index: number): boolean {
    return index === this.focusItemIndex;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.isExpanded) {
      this.displayListbox();
    }

    const nextListItem = this.listItem.get(this.focusItemIndex + 1);
    const prevListItem = this.listItem.get(this.focusItemIndex - 1);

    switch (event.code) {
      case KeyboardEnum.ArrowDown:
        event.preventDefault();
        this.focusItemIndex = nextListItem ? this.focusItemIndex + 1 : 0;

        this.focusItemId = this.listItem.get(
          this.focusItemIndex
        )?.nativeElement?.id;

        this.listItem.get(this.focusItemIndex).nativeElement?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
          inline: 'nearest',
        });
        break;

      case KeyboardEnum.ArrowUp:
        event.preventDefault();
        this.focusItemIndex = prevListItem
          ? this.focusItemIndex - 1
          : this.listItem?.length - 1;

        this.focusItemId = this.listItem.get(
          this.focusItemIndex
        )?.nativeElement?.id;

        this.listItem.get(this.focusItemIndex).nativeElement?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
          inline: 'nearest',
        });
        break;

      case KeyboardEnum.Enter:
        this.listItem
          .get(this.focusItemIndex)
          ?.nativeElement?.dispatchEvent(new Event('click'));
        break;

      case KeyboardEnum.Escape:
        this.clearInput();
        break;

      default:
        break;
    }
  }

  public selectItem(item: AutocompleteV2Suggestion<string>): void {
    this.focusItemId = null;

    if (!this.autoFilter) {
      this.selectItemEvent.emit(item);
      return;
    }

    this.isExpanded = false;
    this.value = item;
    this.input.setValue(item.text);
  }

  public writeValue(value: AutocompleteV2Suggestion<string>): void {
    if (!value?.keepListOpen) {
      this.isExpanded = false;
    }

    this.innerValue = value;
    this.input.setValue(value?.text);
  }
}
